import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const ROLES = [
  { id:"md", label:"MD - Medical Doctor" },
  { id:"np", label:"NP - Nurse Practitioner" },
  { id:"rn", label:"RN - Registered Nurse" },
  { id:"pa", label:"PA - Physician Assistant" },
  { id:"esthetician", label:"Esthetician" },
  { id:"laser_tech", label:"Laser Technician" },
  { id:"provider", label:"Provider" },
  { id:"receptionist", label:"Receptionist" },
];

export const PROVIDER_COLORS = ["#b48c3c","#2563eb","#7c3aed","#2e8b57","#c0392b","#e67e22","#16a085","#d35400","#8e44ad","#34495e"];
const COLORS = PROVIDER_COLORS;

const DAYS = [
  { idx:0, label:"Sunday", short:"Sun" },
  { idx:1, label:"Monday", short:"Mon" },
  { idx:2, label:"Tuesday", short:"Tue" },
  { idx:3, label:"Wednesday", short:"Wed" },
  { idx:4, label:"Thursday", short:"Thu" },
  { idx:5, label:"Friday", short:"Fri" },
  { idx:6, label:"Saturday", short:"Sat" },
];

const inp = {width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export function ProvidersManager() {
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [providerServices, setProviderServices] = useState({});

  useEffect(() => {
    load();
    const ch = supabase.channel("providers-list")
      .on("postgres_changes", { event: "*", schema: "public", table: "providers" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const { data } = await supabase.from("providers").select("*").order("order_index").order("name");
    setProviders(data || []);
    const { data: usersList } = await supabase.from("profiles").select("id,full_name,email,role");
    setUsers(usersList || []);
    const { data: svcs } = await supabase.from("services").select("*").eq("active", true).order("category").order("name");
    setServices(svcs || []);
    const { data: ps } = await supabase.from("provider_services").select("*");
    const map = {};
    (ps||[]).forEach(r => {
      if (!map[r.provider_id]) map[r.provider_id] = [];
      map[r.provider_id].push(r.service_id);
    });
    setProviderServices(map);
    setLoading(false);
  };

  const addNew = () => {
    setSelected({ name:"", title:"", color:COLORS[providers.length % COLORS.length], role:"provider", specialties:[], phone:"", email:"", bio:"", photo_url:"", active:true, _services:[] });
    setShowForm(true);
  };

  const edit = (p) => {
    setSelected({...p, _services: providerServices[p.id] || []});
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this provider? This cannot be undone.")) return;
    await supabase.from("providers").delete().eq("id", id);
  };

  const save = async () => {
    if (!selected.name) { alert("Name is required"); return; }
    const data = {...selected};
    const selectedSvcIds = data._services || [];
    delete data._services;
    delete data.id;
    let providerId = selected.id;
    if (selected.id) {
      await supabase.from("providers").update(data).eq("id", selected.id);
    } else {
      const { data: created } = await supabase.from("providers").insert([data]).select().single();
      providerId = created?.id;
    }
    if (providerId) {
      await supabase.from("provider_services").delete().eq("provider_id", providerId);
      if (selectedSvcIds.length > 0) {
        const rows = selectedSvcIds.map(sid => ({ provider_id: providerId, service_id: sid }));
        await supabase.from("provider_services").insert(rows);
      }
    }
    setShowForm(false);
    setSelected(null);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>Providers</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Manage your team and their weekly availability</div>
        </div>
        <button onClick={addNew} style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Provider</button>
      </div>

      {loading && <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading...</div>}

      {!loading && providers.length === 0 && (
        <div style={{padding:60,textAlign:"center",background:C.light,borderRadius:12,border:"1px dashed "+C.border}}>
          <div style={{fontSize:40,marginBottom:10,opacity:0.3}}>👥</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>No providers yet</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Add your first team member!</div>
          <button onClick={addNew} style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>+ Add Provider</button>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:14}}>
        {providers.map(p => (
          <div key={p.id} style={{background:C.card,border:"1px solid "+C.border,borderLeft:"4px solid "+(p.color||C.gold),borderRadius:12,padding:16,opacity:p.active?1:0.5}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name} style={{width:44,height:44,borderRadius:"50%",objectFit:"cover",border:"2px solid "+(p.color||C.gold),flexShrink:0}}/>
              ) : (
                <div style={{width:44,height:44,borderRadius:"50%",background:(p.color||C.gold)+"20",border:"2px solid "+(p.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:800,color:p.color||C.gold,flexShrink:0}}>
                  {p.name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                </div>
              )}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S,marginBottom:2}}>{p.name}</div>
                <div style={{fontSize:11,color:C.muted,fontWeight:600}}>{p.title || ROLES.find(r=>r.id===p.role)?.label?.split(" - ")[0] || "Provider"}</div>
              </div>
              {!p.active && <div style={{fontSize:9,padding:"2px 8px",background:C.muted,color:"#fff",borderRadius:10,fontWeight:700}}>INACTIVE</div>}
            </div>

            {(p.email || p.phone || p.user_id) && (
              <div style={{fontSize:11,color:C.muted,marginBottom:10,padding:"6px 0",borderTop:"1px solid "+C.border}}>
                {p.email && <div>📧 {p.email}</div>}
                {p.phone && <div>📞 {p.phone}</div>}
                {p.user_id && <div style={{color:C.green,fontWeight:600,marginTop:2}}>🔐 Has login access</div>}
              </div>
            )}

            {p.specialties && p.specialties.length > 0 && (
              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>
                {p.specialties.slice(0,4).map(s => (
                  <span key={s} style={{fontSize:10,padding:"2px 8px",background:(p.color||C.gold)+"15",color:p.color||C.gold,borderRadius:10,fontWeight:600}}>{s}</span>
                ))}
              </div>
            )}

            <div style={{display:"flex",gap:6,marginTop:12,paddingTop:10,borderTop:"1px solid "+C.border}}>
              <button onClick={()=>edit(p)} style={{flex:1,padding:"7px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit Info</button>
              <ProviderHoursButton providerId={p.id} providerColor={p.color||C.gold}/>
              <button onClick={()=>remove(p.id)} style={{padding:"7px 10px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>🗑</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && selected && (
        <ProviderFormModal provider={selected} setProvider={setSelected} onClose={()=>{setShowForm(false);setSelected(null);}} onSave={save} users={users} services={services}/>
      )}
    </div>
  );
}

function ProviderHoursButton({ providerId, providerColor }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <button onClick={()=>setShow(true)} style={{flex:1,padding:"7px",background:providerColor+"15",color:providerColor,border:"1px solid "+providerColor+"40",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>📅 Hours</button>
      {show && <ProviderHoursModal providerId={providerId} onClose={()=>setShow(false)}/>}
    </>
  );
}

export function ProviderHoursModal({ providerId, onClose }) {
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, [providerId]);

  const load = async () => {
    const { data } = await supabase.from("provider_weekly_hours").select("*").eq("provider_id", providerId);
    const map = {};
    DAYS.forEach(d => {
      const existing = (data||[]).find(h => h.day_of_week === d.idx);
      map[d.idx] = existing || { day_of_week: d.idx, available: false, start_time: "09:00", end_time: "17:00" };
    });
    setHours(map);
    setLoading(false);
  };

  const toggle = (idx) => {
    setHours({...hours, [idx]: {...hours[idx], available: !hours[idx].available}});
  };

  const update = (idx, field, value) => {
    setHours({...hours, [idx]: {...hours[idx], [field]: value}});
  };

  const save = async () => {
    setSaving(true);
    await supabase.from("provider_weekly_hours").delete().eq("provider_id", providerId);
    const rows = Object.values(hours).map(h => ({
      provider_id: providerId,
      day_of_week: h.day_of_week,
      start_time: h.start_time,
      end_time: h.end_time,
      available: h.available
    }));
    await supabase.from("provider_weekly_hours").insert(rows);
    setSaving(false);
    onClose();
  };

  if (loading) return null;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:520,padding:24,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>📅 Weekly Availability</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:18}}>Set regular working hours for each day</div>

        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {DAYS.map(d => {
            const h = hours[d.idx];
            return (
              <div key={d.idx} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:h.available?C.goldBg:C.light,border:"1px solid "+(h.available?C.borderGold:C.border),borderRadius:8}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",minWidth:130,flexShrink:0}}>
                  <input type="checkbox" checked={h.available} onChange={()=>toggle(d.idx)} style={{accentColor:C.gold,width:16,height:16,cursor:"pointer"}}/>
                  <span style={{fontSize:13,fontWeight:600,color:C.text}}>{d.label}</span>
                </label>
                {h.available && (
                  <>
                    <input type="time" value={h.start_time||"09:00"} onChange={e=>update(d.idx,"start_time",e.target.value)} style={{...inp,padding:"6px 8px",fontSize:12,flex:1}}/>
                    <span style={{fontSize:11,color:C.muted}}>to</span>
                    <input type="time" value={h.end_time||"17:00"} onChange={e=>update(d.idx,"end_time",e.target.value)} style={{...inp,padding:"6px 8px",fontSize:12,flex:1}}/>
                  </>
                )}
                {!h.available && <span style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>Day off</span>}
              </div>
            );
          })}
        </div>

        <div style={{display:"flex",gap:8,marginTop:18,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={save} disabled={saving} style={{padding:"9px 16px",background:saving?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:saving?C.muted:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F}}>{saving?"Saving...":"Save Hours"}</button>
        </div>
      </div>
    </div>
  );
}

export function ProviderFormModal({ provider, setProvider, onClose, onSave, users, services }) {
  const servicesByCategory = {};
  (services||[]).forEach(s => {
    if (!servicesByCategory[s.category]) servicesByCategory[s.category] = [];
    servicesByCategory[s.category].push(s);
  });
  const selectedSvcs = provider._services || [];
  const toggleService = (id) => {
    const newList = selectedSvcs.includes(id) ? selectedSvcs.filter(x=>x!==id) : [...selectedSvcs, id];
    setProvider({...provider, _services: newList});
  };
  const toggleCategory = (cat) => {
    const catIds = servicesByCategory[cat].map(s=>s.id);
    const allIn = catIds.every(id => selectedSvcs.includes(id));
    const newList = allIn 
      ? selectedSvcs.filter(id => !catIds.includes(id))
      : [...new Set([...selectedSvcs, ...catIds])];
    setProvider({...provider, _services: newList});
  };
  const [specInput, setSpecInput] = useState("");

  const addSpec = () => {
    if (!specInput.trim()) return;
    setProvider({...provider, specialties: [...(provider.specialties||[]), specInput.trim()]});
    setSpecInput("");
  };

  const removeSpec = (s) => {
    setProvider({...provider, specialties: (provider.specialties||[]).filter(x=>x!==s)});
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:520,padding:24,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:16}}>{provider.id?"Edit Provider":"Add Provider"}</div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <PhotoUploader provider={provider} setProvider={setProvider}/>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Full Name *</label>
            <input value={provider.name} onChange={e=>setProvider({...provider,name:e.target.value})} placeholder="Jane Doe" style={inp}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Role</label>
              <select value={provider.role} onChange={e=>setProvider({...provider,role:e.target.value})} style={inp}>
                {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Display Title</label>
              <input value={provider.title||""} onChange={e=>setProvider({...provider,title:e.target.value})} placeholder="e.g. Dr. / NP / RN" style={inp}/>
            </div>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:8}}>Color</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {COLORS.map(c => (
                <button key={c} onClick={()=>setProvider({...provider,color:c})} style={{width:32,height:32,borderRadius:"50%",background:c,border:provider.color===c?"3px solid #000":"2px solid #fff",boxShadow:"0 0 0 1px "+C.border,cursor:"pointer"}}/>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Phone</label>
              <input value={provider.phone||""} onChange={e=>setProvider({...provider,phone:e.target.value})} placeholder="(555) 123-4567" style={inp}/>
            </div>
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Email</label>
              <input type="email" value={provider.email||""} onChange={e=>setProvider({...provider,email:e.target.value})} placeholder="jane@novamedspa.com" style={inp}/>
            </div>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Specialties</label>
            <div style={{display:"flex",gap:4,marginBottom:8}}>
              <input value={specInput} onChange={e=>setSpecInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addSpec();}}} placeholder="Botox, Filler, Laser..." style={{...inp,flex:1}}/>
              <button type="button" onClick={addSpec} style={{padding:"8px 14px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>+ Add</button>
            </div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {(provider.specialties||[]).map(s => (
                <span key={s} style={{fontSize:11,padding:"3px 8px",background:C.goldBg,color:C.gold,borderRadius:10,fontWeight:600,display:"inline-flex",alignItems:"center",gap:4}}>
                  {s}
                  <button onClick={()=>removeSpec(s)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:14,padding:0,lineHeight:1}}>×</button>
                </span>
              ))}
            </div>
          </div>
          <div style={{padding:14,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10}}>
            <div style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",marginBottom:8}}>Services This Provider Offers</div>
            <div style={{fontSize:10,color:C.muted,marginBottom:12,fontStyle:"italic"}}>Only these services will be bookable with this provider</div>
            {Object.keys(servicesByCategory).length === 0 && (
              <div style={{padding:10,textAlign:"center",fontSize:11,color:C.muted}}>No active services. Add services in the Services tab first.</div>
            )}
            <div style={{maxHeight:260,overflowY:"auto",paddingRight:4}}>
              {Object.entries(servicesByCategory).map(([cat, svcs]) => {
                const catIds = svcs.map(s=>s.id);
                const allIn = catIds.every(id => selectedSvcs.includes(id));
                const someIn = catIds.some(id => selectedSvcs.includes(id));
                return (
                  <div key={cat} style={{marginBottom:10,background:"#fff",borderRadius:8,padding:10}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:6,paddingBottom:6,borderBottom:"1px solid "+C.border}}>
                      <input type="checkbox" checked={allIn} ref={el=>{if(el)el.indeterminate=someIn&&!allIn;}} onChange={()=>toggleCategory(cat)} style={{accentColor:C.gold,width:14,height:14,cursor:"pointer"}}/>
                      <span style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase"}}>{cat}</span>
                      <span style={{fontSize:10,color:C.muted,marginLeft:"auto"}}>{svcs.filter(s=>selectedSvcs.includes(s.id)).length}/{svcs.length}</span>
                    </label>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                      {svcs.map(s => (
                        <label key={s.id} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"4px 6px",borderRadius:5,background:selectedSvcs.includes(s.id)?C.goldBg:"transparent"}}>
                          <input type="checkbox" checked={selectedSvcs.includes(s.id)} onChange={()=>toggleService(s.id)} style={{accentColor:C.gold,cursor:"pointer"}}/>
                          <span style={{fontSize:11,color:C.text}}>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Link to User Account (optional)</label>
            <select value={provider.user_id||""} onChange={e=>setProvider({...provider,user_id:e.target.value||null})} style={inp}>
              <option value="">— No login (external/display only)</option>
              {(users||[]).map(u => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>)}
            </select>
            <div style={{fontSize:10,color:C.muted,marginTop:4,fontStyle:"italic"}}>Link if this provider needs to log in and view their schedule</div>
          </div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Bio (shown on booking page)</label>
            <textarea value={provider.bio||""} onChange={e=>setProvider({...provider,bio:e.target.value})} rows={2} placeholder="Experienced aesthetic provider..." style={{...inp,resize:"vertical"}}/>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 14px",background:C.light,borderRadius:8}}>
            <input type="checkbox" checked={provider.active} onChange={e=>setProvider({...provider,active:e.target.checked})} style={{accentColor:C.gold}}/>
            <span style={{fontSize:12,color:C.text,fontWeight:600}}>Active (accepting appointments)</span>
          </label>
        </div>

        <div style={{display:"flex",gap:8,marginTop:18,justifyContent:"flex-end"}}>
          <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
          <button onClick={onSave} style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>{provider.id?"Save Changes":"Create Provider"}</button>
        </div>
      </div>
    </div>
  );
}

function PhotoUploader({ provider, setProvider }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Image must be under 5MB"); return; }
    setErr("");
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `provider-${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
      const { error: upErr } = await supabase.storage.from("provider-photos").upload(path, file, { cacheControl: "3600", upsert: false });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("provider-photos").getPublicUrl(path);
      setProvider({...provider, photo_url: pub.publicUrl});
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => setProvider({...provider, photo_url: ""});

  const hasPhoto = !!provider.photo_url;

  return (
    <div style={{padding:14,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10}}>
      <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:10}}>Provider Photo</label>
      <div style={{display:"flex",alignItems:"center",gap:14}}>
        {hasPhoto ? (
          <img src={provider.photo_url} alt="Provider" style={{width:80,height:80,borderRadius:"50%",objectFit:"cover",border:"3px solid "+(provider.color||C.gold),flexShrink:0}}/>
        ) : (
          <div style={{width:80,height:80,borderRadius:"50%",background:(provider.color||C.gold)+"20",border:"3px dashed "+(provider.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:provider.color||C.gold,flexShrink:0,textAlign:"center",lineHeight:1.2}}>No<br/>Photo</div>
        )}
        <div style={{flex:1}}>
          <label style={{display:"inline-block",padding:"8px 14px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:7,fontSize:11,fontWeight:700,cursor:uploading?"not-allowed":"pointer",fontFamily:F,opacity:uploading?0.6:1}}>
            {uploading?"Uploading...":(hasPhoto?"Replace photo":"Upload photo")}
            <input type="file" accept="image/*" onChange={onPick} disabled={uploading} style={{display:"none"}}/>
          </label>
          {hasPhoto && (
            <button type="button" onClick={removePhoto} style={{marginLeft:6,padding:"8px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Remove</button>
          )}
          <div style={{fontSize:10,color:C.muted,marginTop:6,lineHeight:1.4}}>JPG, PNG up to 5MB. Square photos look best (recommended 400×400px).</div>
          {err && <div style={{fontSize:10,color:C.red,marginTop:4,fontWeight:600}}>{err}</div>}
        </div>
      </div>
    </div>
  );
}
